import {
  AccountInfo,
  IPublicClientApplication,
  SilentRequest
} from '@azure/msal-browser';
import jwtDecode from 'jwt-decode';
import { RouteComponentProps } from 'react-router-dom';
import { Request, Response, signInExt, verifySCGUser } from '../../api/auths';
import { AppDispatch } from '../../app/store';
import { ACTIVE_ACCOUNT_KEY } from '../../constants';
import userProfileSlice, {
  UserContextType
} from '../UserProfile/userProfileSlice';
import { loginRequest } from './authConfig';

export type System = 'azure-scg';

export function getUser() {
  const token = getToken();
  if (token) {
    const { user }: UserContextType = jwtDecode(token);
    return user;
  }
  return null;
}

export function getToken() {
  return localStorage.getItem('token');
}

export function getExp() {
  const token = getToken();
  if (token) {
    const { exp }: UserContextType = jwtDecode(token);
    return exp;
  }
  return null;
}

export function getUserType() {
  return localStorage.getItem('user-type');
}

export function getActiveAccount() {
  return localStorage.getItem(ACTIVE_ACCOUNT_KEY);
}

export function getCoverPic() {
  return localStorage.getItem('coverPic');
}

export function getProfilePic() {
  return localStorage.getItem('profilePic');
}

// Function to handle silent authentication and account sync
export async function silentSyncActiveAccount(
  instance: IPublicClientApplication,
  activeAccountKey: string
) {
  try {
    const accounts = instance.getAllAccounts(); // Get all available accounts
    const account = accounts.find(
      (account) => account.homeAccountId === activeAccountKey
    ); // Find the matching account

    if (account) {
      const silentRequest: SilentRequest = { account, scopes: ['User.Read'] };
      return await instance.acquireTokenSilent(silentRequest); // Silent auth
    } else {
      // Trying login...
      try {
        return await instance.loginPopup(loginRequest); // Prompt login if account not found
      } catch (popupError: any) {
        // Check for popup blocker or interaction error
        if (
          popupError?.errorMessage?.includes('popup_window_error') || // generic error
          popupError?.errorCode === 'popup_window_error' || // specific error from MSAL
          popupError?.message?.includes('The popup window was closed') // possible user action
        ) {
          alert(
            'Login popup was blocked or closed. Please enable popups and try again.'
          );
        } else {
          console.error('Login popup failed:', popupError);
        }
        throw popupError; // rethrow so caller knows it failed
      }
    }
  } catch (error) {
    console.error('Silent sync failed:', error);
  }
}

// For SCG Login using Azure AD
export function signInSCGRedirect(instance: IPublicClientApplication) {
  instance.loginRedirect(loginRequest);
}

export function autoSignInRedirect(
  instance: IPublicClientApplication,
  activeAccountKey: string
) {
  const userType = localStorage.getItem('user-type');
  if (userType === 'scg') {
    silentSyncActiveAccount(instance, activeAccountKey);
  }
}

export async function signInSCG({
  accessTokenRequest,
  instance
}: {
  accessTokenRequest: {
    scopes: string[];
    account: AccountInfo;
  };
  instance: IPublicClientApplication;
}): Promise<UserContextType> {
  localStorage.setItem('user-type', 'scg');
  const accessTokenResponse = await instance.acquireTokenSilent(
    accessTokenRequest
  );

  // Call User Management API for verify access token
  const response = await verifySCGUser(accessTokenResponse.accessToken);
  return decodeResponse(response);
}

export async function signInExternal(request: Request) {
  localStorage.setItem('user-type', 'external');
  const response = await signInExt(request);
  return decodeResponse(response);
}

function decodeResponse(response: Response): UserContextType {
  const { token } = response.data;
  const { user, exp }: UserContextType = jwtDecode(token);

  localStorage.setItem('token', token);
  return { user, token, exp };
}

export function forceSignOut(dispatch: AppDispatch) {
  localStorage.removeItem('token');
  dispatch(userProfileSlice.actions.clear());
}

export function signOut({
  dispatch,
  instance
}: {
  dispatch: AppDispatch;
  instance: IPublicClientApplication;
}) {
  try {
    // Check user type before logout
    const userType = getUserType();
    // Logout for SCG Azure user
    if (userType === 'scg') {
      const activeAccountKey = getActiveAccount();
      const accounts = instance.getAllAccounts(); // Get all available accounts
      const account = accounts.find(
        (account) => account.homeAccountId === activeAccountKey
      ); // Find the matching account

      if (activeAccountKey) localStorage.removeItem(ACTIVE_ACCOUNT_KEY);
      instance.setActiveAccount(null);
      instance.logoutRedirect({ account });
    } else {
      // Logout for external user
      window.location.href = `${process.env.REACT_APP_POST_LOGOUT_REDIRECT_URI}`;
    }
  } catch (error) {
    console.error('Sign-out failed:', error);
  } finally {
    localStorage.removeItem('user-type');
    localStorage.removeItem('token');
    localStorage.removeItem('coverPic');
    dispatch(userProfileSlice.actions.clear());
  }
}

export function saveRedirectUrl(redirectUrl: string): void {
  localStorage.setItem('redirectUrl-backspace', redirectUrl);
}

export function redirectAfterLogin(
  history: RouteComponentProps['history']
): void {
  const redirectUrl = localStorage.getItem('redirectUrl-backspace');

  if (redirectUrl) {
    history.push(redirectUrl);
    localStorage.removeItem('redirectUrl-backspace');
  } else {
    history.push('/');
  }
}
